import React, { useEffect, useMemo, useState } from 'react';

import PageHeader from 'src/components/organisms/PageHeader';
import Footer from 'src/components/molecules/footer/Footer';
import ContentCardsContainer, { ContentCard } from 'src/components/organisms/ContentCard';
import InfoIcon from '@mui/icons-material/Info';

import CustomJoyride from 'src/components/organisms/CustomJoyride';
import CustomButton from 'src/components/atoms/buttons/CustomButton';
import useTour from 'src/hooks/tours/useTour';
import { useSettingsContext } from 'src/hooks/useSettings';
import {
  useGetDashboardShipped,
  useGetDashboardPurchasing,
  useGetDashboardReturns,
  useGetDashboardSales,
  useGetDashboardToShip,
  useGetDashboardCreditNotes,
  useGetDashboardSalesMargins,
} from 'src/hooks/api/requestConfigs/dashboard';

import {
  buildDashboardSalesProvider,
  buildDashboardReturnsProvider,
  buildDashboardDispatchedProvider,
  buildDashboardToShipProvider,
  buildDashboardPurchasingProvider,
  buildDashboardCreditNotesProvider,
  composeProviders,
} from './DashboardProviders';
import { DashboardGraph } from './DashboardGraph';
import {
  DashboardCards,
  DashboardExecutiveSummaryCard,
  DashboardChannelBreakdownCard,
  DashboardOperationsOverviewCard,
  useDashboardCardMetrics,
} from './DashboardCards';
import {
  DashboardMetricFilter,
  DashboardPeriodFilter,
  DashboardSalesChannelFilter,
  DashboardSalesRepFilter,
} from './DashboardFilters';
import type { DashboardBarKey, KpiEntry, Period, SalesChannel } from './DashboardTypes';
import { buildRangeTitle, DASHBOARD_SALES_REPS, useIsMobile } from './DashboardHelpers';
import { buildKpiListFromSettings, KpiOverview2 } from './DashboardModules';
import ThemeSelector from 'src/components/molecules/settings/ThemeSettingButton';
import { useTranslation } from 'react-i18next';
import internationalization from 'src/i18n/i18n';
import LanguageIdSetting from 'src/components/molecules/settings/LanguageSetting';

const DESKTOP_CHART_CARD_H = 520;
const DESKTOP_SMALL_CARD_H = 320;
const DEFAULT_DISPLAY_LANGUAGE = 1;

const mapLanguageIdToI18nCode = (id?: number) => {
  switch (Number(id)) {
    case 2:
      return 'se';
    case 7:
      return 'de';
    case 1:
    default:
      return 'en';
  }
};

const DashboardMain: React.FC = () => {
  const isMobile = useIsMobile(768);
  const { settings } = useSettingsContext();
  const { t } = useTranslation();

  const date = new Date();
  const toDate = date.toISOString().split('T')[0];
  date.setFullYear(date.getFullYear() - 1);
  const dashboardFromDate = date.toISOString().split('T')[0];

  const displayLanguageId = Number((settings as any)?.DisplayLanguage ?? DEFAULT_DISPLAY_LANGUAGE);

  useEffect(() => {
    const languageCode = mapLanguageIdToI18nCode(displayLanguageId);
    if (internationalization.language !== languageCode) {
      internationalization.changeLanguage(languageCode);
    }
  }, [displayLanguageId]);

  const [chartPeriod, setChartPeriod] = useState<Period>('Week');
  const [selectedBars, setSelectedBars] = useState<DashboardBarKey[]>(['ordersBar', 'ordersValueBar']);
  const [selectedSalesChannels, setSelectedSalesChannels] = useState<SalesChannel[]>(['Online', 'Store']);
  const [viewBySalesRep] = useState(false);
  const [selectedSalesReps, setSelectedSalesReps] = useState<string[]>(DASHBOARD_SALES_REPS.map(rep => rep.id));

  const kpiSettings = (settings as any)?.KPI ?? (settings as any)?.Kpi ?? (settings as any) ?? {};

  const sales = useGetDashboardSales(dashboardFromDate, toDate) ?? [];
  const returns = useGetDashboardReturns(dashboardFromDate) ?? [];
  const shipped = useGetDashboardShipped(dashboardFromDate, toDate) ?? [];
  const toShip = useGetDashboardToShip(dashboardFromDate) ?? [];
  const purchasing = useGetDashboardPurchasing(dashboardFromDate) ?? [];
  const creditNotes = useGetDashboardCreditNotes(dashboardFromDate) ?? [];
  const salesMargins = useGetDashboardSalesMargins(dashboardFromDate,toDate) ?? [];

  const dataProvider = useMemo(
    () =>
      composeProviders(
        buildDashboardSalesProvider(sales as any),
        buildDashboardReturnsProvider(returns as any),
        buildDashboardDispatchedProvider(shipped as any),
        buildDashboardToShipProvider(toShip as any),
        buildDashboardPurchasingProvider(purchasing as any),
        buildDashboardCreditNotesProvider(creditNotes as any),
      ),
    [creditNotes, purchasing, returns, sales, shipped, toShip]
  );

  const tourProps = useTour ? useTour(['Dashboard', 'Purchasing Intro']) : { restartTour: () => console.log('Restart Tour Placeholder') };
  const rangeTitle = useMemo(() => buildRangeTitle(chartPeriod), [chartPeriod]);

  useEffect(() => {
    if (viewBySalesRep) {
      setSelectedSalesReps(DASHBOARD_SALES_REPS.map(rep => rep.id));
      setSelectedBars([]);
      setSelectedSalesChannels([]);
      return;
    }

    setSelectedSalesReps([]);
    setSelectedBars(previous => (previous.length ? previous : ['ordersBar']));
    setSelectedSalesChannels(previous => (previous.length ? previous : ['Online', 'Store']));
  }, [viewBySalesRep]);

  const { yearlyGoal, dailyAvgSpent, dailyBudget } = useMemo(() => {
    const budgetCfg = kpiSettings?.PurchasesVsBudgetYTD;
    const now = new Date();
    const year = now.getFullYear();
    const startOfYear = new Date(year, 0, 1);
    const dayOfYear = Math.floor((+new Date(year, now.getMonth(), now.getDate()) - +startOfYear) / 86400000) + 1;
    const isLeap = ((year % 4 === 0) && (year % 100 !== 0)) || (year % 400 === 0);
    const daysInYear = isLeap ? 366 : 365;
    const yearlyGoal = Number(budgetCfg?.threshold ?? 0) || 0;

    const ytdPurchases = purchasing.reduce((acc, purchaseOrder) => {
      const created = purchaseOrder?.PurchaseOrderCreated ? new Date(purchaseOrder.PurchaseOrderCreated) : null;
      if (!created) return acc;
      if (created.getFullYear() !== year) return acc;
      if (created > now) return acc;
      return acc + (Number(purchaseOrder?.OrderValueInDefaultCurreny ?? 0) || 0);
    }, 0);

    const dailyBudget = yearlyGoal / (daysInYear || 1);
    const dailyAvgSpent = ytdPurchases / (dayOfYear || 1);
    return { yearlyGoal, dailyAvgSpent, dailyBudget };
  }, [purchasing, kpiSettings]);

  const kpiList: KpiEntry[] = useMemo(() => {
    const baseKpis = buildKpiListFromSettings({
      period: chartPeriod,
      dashboardSales: sales as any,
      dashboardReturns: returns as any,
      settingsKpi: kpiSettings,
    });

    const filtered = baseKpis.filter(kpi => kpi.id !== 'PurchasesVsBudgetYTD');

    if (yearlyGoal > 0 && Number.isFinite(dailyAvgSpent) && Number.isFinite(dailyBudget)) {
      const deltaPctDay = dailyBudget > 0 ? ((dailyAvgSpent - dailyBudget) / dailyBudget) * 100 : 0;

      filtered.unshift({
        id: 'PurchasesDailyAverage',
        title: t('Purchasing (avg/day)'),
        value: dailyAvgSpent,
        target: dailyBudget,
        unit: t('SEK/day'),
        percent: deltaPctDay,
        status: Math.abs(deltaPctDay) <= 5 ? 'success' : Math.abs(deltaPctDay) <= 10 ? 'warning' : 'error',
        tooltip: '',
      } as KpiEntry);
    }

    return filtered;
  }, [chartPeriod, dailyAvgSpent, dailyBudget, returns, sales, kpiSettings, yearlyGoal, t]);

  const cardMetrics = useDashboardCardMetrics({
    dataProvider,
    period: chartPeriod,
    dashboardSales: sales,
    dashboardReturns: returns,
    dashboardPurchasing: purchasing,
    dashboardDispatched: shipped,
    dashboardToShip: toShip,
    dashboardCreditNotes: creditNotes,
    dashboardSalesMargins: salesMargins,
  });

  const headerComponents = [
    !viewBySalesRep && {
      component: <DashboardSalesChannelFilter value={selectedSalesChannels} onChange={setSelectedSalesChannels} />,
      isInGroupedMenu: isMobile,
    },
    {
      component: viewBySalesRep
        ? <DashboardSalesRepFilter value={selectedSalesReps} onChange={setSelectedSalesReps} salesReps={DASHBOARD_SALES_REPS} />
        : <DashboardMetricFilter value={selectedBars} onChange={setSelectedBars} />,
      isInGroupedMenu: isMobile,
    },
    {
      component: <DashboardPeriodFilter value={chartPeriod} onChange={setChartPeriod} />,
      isInGroupedMenu: isMobile,
    },
    {
      component: <CustomButton iconButton title={t('Help')} icon={<InfoIcon />} onClick={() => tourProps.restartTour()} />
    },
  ].filter(Boolean);

  return (
    <>
      {CustomJoyride && <CustomJoyride tourProps={tourProps} />}

      <PageHeader
        {...{
          title: t('Dashboard'),
          subTitle: t('Charts, stats and KPIs'),
          components: [
            { component: <ThemeSelector /> },
            { component: <LanguageIdSetting settingKey="DisplayLanguage" allowedLanguageIds={[1, 2]} tooltip={t('Display Language')} /> },
            ...headerComponents,
          ],
        }}
      />

      {isMobile ? (
        <DashboardCards
          chartPeriod={chartPeriod}
          selectedBars={selectedBars}
          selectedSalesChannels={selectedSalesChannels}
          viewBySalesRep={viewBySalesRep}
          selectedSalesReps={selectedSalesReps}
          salesRepsData={DASHBOARD_SALES_REPS}
          dataProvider={dataProvider}
          dashboardSales={sales}
          dashboardReturns={returns}
          dashboardPurchasing={purchasing}
          dashboardDispatched={shipped}
          dashboardToShip={toShip}
          dashboardCreditNotes={creditNotes}
          dashboardSalesMargins={salesMargins}
        />
      ) : (
        <ContentCardsContainer>
          <ContentCard
            contentPadding={0}
            xs={12}
            lg={12}
            xl={9}
            cardId="order-chart"
            cardHeader={`${t('Orders Overview')} — ${rangeTitle}`}
            cardTypes={[]}
          >
            <DashboardGraph
              height={DESKTOP_CHART_CARD_H}
              period={chartPeriod}
              selectedBars={selectedBars}
              selectedSalesChannels={selectedSalesChannels}
              viewBySalesRep={viewBySalesRep}
              selectedSalesReps={selectedSalesReps}
              salesRepsData={DASHBOARD_SALES_REPS}
              dataProvider={dataProvider}
            />
          </ContentCard>

          <DashboardExecutiveSummaryCard
            metrics={cardMetrics}
            period={chartPeriod}
            sizes={{ xs: 12, lg: 12, xl: 3 }}
            minHeight={DESKTOP_SMALL_CARD_H}
          />

          <DashboardChannelBreakdownCard
            metrics={cardMetrics}
            period={chartPeriod}
            sizes={{ xs: 12, md: 6, lg: 6, xl: 4 }}
            minHeight={DESKTOP_SMALL_CARD_H}
          />

          <DashboardOperationsOverviewCard
            metrics={cardMetrics}
            period={chartPeriod}
            sizes={{ xs: 12, md: 6, lg: 6, xl: 4 }}
            minHeight={DESKTOP_SMALL_CARD_H}
          />

          <ContentCard xs={12} md={12} lg={12} xl={4} cardId="Kpi2" cardHeader={t('KPI Overview')} cardTypes={[]}>
            <KpiOverview2 kpis={kpiList} />
          </ContentCard>
        </ContentCardsContainer>
      )}

      <Footer />
    </>
  );
};

export default DashboardMain;